import { del, get, post, put } from "@/api/client";

export type SegmentOp = "eq" | "neq" | "in" | "contains" | "exists";

export interface SegmentCondition {
  attribute: string;
  op: SegmentOp;
  value?: string | null;
  values?: string[] | null;
}

export interface SegmentRule {
  match: "all" | "any";
  conditions: SegmentCondition[];
}

export interface Segment {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  rule: SegmentRule;
  created_at: string;
  updated_at: string;
}

export interface SegmentMember {
  user_id: string;
  full_name: string;
  job_title: string | null;
  department: string | null;
  location: string | null;
}

export interface SegmentMembers {
  count: number;
  members: SegmentMember[];
}

export interface SegmentInput {
  name: string;
  description?: string | null;
  rule: SegmentRule;
}

export const SEGMENT_ATTRIBUTES = [
  "department",
  "team",
  "location",
  "job_title",
  "employment_type",
  "seniority",
  "language",
  "tags",
] as const;

async function unwrap<T>(p: Promise<{ ok: true; data: T } | { ok: false; err: { message: string } }>): Promise<T> {
  const r = await p;
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export const listSegments = (signal?: AbortSignal) => unwrap<Segment[]>(get("/api/segments", signal));
export const getSegment = (id: string, signal?: AbortSignal) => unwrap<Segment>(get(`/api/segments/${id}`, signal));
export const createSegment = (input: SegmentInput) => unwrap<Segment>(post("/api/segments", input));
export const updateSegment = (id: string, input: SegmentInput) => unwrap<Segment>(put(`/api/segments/${id}`, input));
export const deleteSegment = (id: string) => del(`/api/segments/${id}`);
export const previewSegment = (rule: SegmentRule) => unwrap<SegmentMembers>(post("/api/segments/preview", { rule }));
export const segmentMembers = (id: string, signal?: AbortSignal) =>
  unwrap<SegmentMembers>(get(`/api/segments/${id}/members`, signal));
